import React from "react";
import { authorizedFetch } from "./boot.js";
import { Card } from "./ui.jsx";

async function call(path, input, key) {
  const response = await authorizedFetch(path, input ? {
    method: "POST", headers: { "Content-Type": "application/json", "Idempotency-Key": key },
    body: JSON.stringify({ input }),
  } : undefined);
  const data = await response.json();
  if (!response.ok || (input && data.state !== "COMPLETED")) {
    const error = new Error("Unavailable"); error.status = response.status; throw error;
  }
  return data;
}

function useBook(path, valid) {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [pending, setPending] = React.useState(null);
  const guard = React.useRef(false);
  const alive = React.useRef(true);
  const read = async () => {
    const value = await call(path);
    if (!valid(value)) throw new Error("Wrong scope");
    if (alive.current) setData(value);
    return value;
  };
  React.useEffect(() => {
    alive.current = true;
    read().catch(() => { if (alive.current) { setData(null); setError("The address book could not be loaded. Refresh to try again."); } });
    return () => { alive.current = false; };
  }, [path]);
  async function refresh() {
    if (guard.current) return;
    guard.current = true; setBusy(true); setError("");
    try { await read(); } catch { setData(null); setError("The address book could not be loaded."); }
    finally { guard.current = false; if (alive.current) setBusy(false); }
  }
  async function save(attempt) {
    if (guard.current) return false;
    guard.current = true; setBusy(true); setError("");
    let confirmed = false;
    try {
      await call(attempt.path, attempt.input, attempt.key);
      confirmed = true;
      if (!alive.current) return false;
      setPending(null); setData(null);
      await read(); return true;
    } catch (failure) {
      if (!alive.current) return false;
      if (confirmed) setError("Saved, but the updated list could not be loaded. Refresh before editing again.");
      else if ([400, 403, 404, 409, 412].includes(failure.status)) {
        setPending(null); setData(null);
        setError("The list changed or this edit is not allowed. Refresh and review before saving again.");
      } else {
        setPending(attempt); setError("The save could not be confirmed. Retry the same save before editing again.");
      }
      return false;
    } finally { guard.current = false; if (alive.current) setBusy(false); }
  }
  return { data, busy, error, pending, refresh, save };
}

function BookStatus({ book }) {
  return <>
    {book.error && <p role="alert">{book.error}</p>}
    {!book.data && !book.error && <p role="status">Loading names…</p>}
    {book.pending && <button className="btn" disabled={book.busy} onClick={() => book.save(book.pending)}>Retry same save</button>}
    <button className="btn sm" disabled={book.busy || Boolean(book.pending)} onClick={book.refresh}>Refresh names</button>
  </>;
}

export function InspectorBook({ sale, vendor }) {
  const book = useBook(`/collections/vendor.inspectors.book@1/?${new URLSearchParams({ sale })}`, value => value.sale === sale && value.vendor === vendor);
  const [name, setName] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [selected, setSelected] = React.useState(null);
  const [rename, setRename] = React.useState("");
  const [target, setTarget] = React.useState("");
  const [confirm, setConfirm] = React.useState(null);
  const entries = book.data?.entries || [];
  const locked = book.busy || Boolean(book.pending) || !book.data;
  const allowed = action => !locked && book.data?.permissions[action];
  async function save(action, input) {
    if (await book.save({ path: `/actions/vendor.inspectors.${action}@1/`, input, key: window.crypto.randomUUID() })) {
      setSelected(null); setConfirm(null); setName("");
    }
  }
  return <Card title="Your inspector address book" subtitle="Private to your business · kept across sales">
    <p>Correct a spelling here and linked inspections show that name. Original entries remain in the audit history.</p>
    <BookStatus book={book}/>
    {book.data && <>
      <form className="book-add" onSubmit={event => { event.preventDefault(); if (allowed("add") && name.trim()) save("add", { name: name.trim() }); }}>
        <label>New inspector<input value={name} maxLength={255} disabled={locked} onChange={event => setName(event.target.value)}/></label>
        <button className="btn primary" disabled={!allowed("add") || !name.trim()}>Add name</button>
      </form>
      <label className="field">Find a name<input type="search" value={search} onChange={event => setSearch(event.target.value)}/></label>
      {!entries.length && <p>Names are added here when you log an inspection, or you can add one above.</p>}
      {entries.filter(row => row.name.toLowerCase().includes(search.toLowerCase())).map(row => <div className="book-row" key={row.id}>
        <div><strong>{row.name}</strong>{row.retired && <span className="caption"> · retired</span>}</div>
        <button className="btn sm" disabled={locked} onClick={() => { setSelected(row.id); setRename(row.name); setTarget(""); setConfirm(null); }}>Manage</button>
        {selected === row.id && <div className="book-editor">
          <label>Spelling<input value={rename} maxLength={255} disabled={locked} onChange={event => setRename(event.target.value)}/></label>
          <button className="btn" disabled={!allowed("rename") || !rename.trim() || rename.trim() === row.name} onClick={() => save("rename", { inspector_id: row.id, name: rename.trim(), expected_revision: row.revision })}>Save spelling</button>
          {row.retired ? <button className="btn" disabled={!allowed("add")} onClick={() => save("add", { name: row.name })}>Restore name</button>
            : <button className="btn" disabled={!allowed("retire")} onClick={() => setConfirm({ action: "retire", row })}>Retire name</button>}
          <label>Merge into<select value={target} disabled={locked} onChange={event => { setTarget(event.target.value); setConfirm(null); }}>
            <option value="">Choose the name to keep</option>
            {entries.filter(other => other.id !== row.id && !other.retired).map(other => <option key={other.id} value={other.id}>{other.name}</option>)}
          </select></label>
          <button className="btn" disabled={!allowed("merge") || !target} onClick={() => setConfirm({ action: "merge", row, target })}>Review merge</button>
          {confirm?.row.id === row.id && <div role="region" aria-label="Confirm name change">
            <p>{confirm.action === "merge" ? `Merge ${row.name} into ${entries.find(other => other.id === confirm.target)?.name}? Past linked inspections will use the name you keep.` : `Retire ${row.name}? Past inspections stay visible; the name stops appearing in suggestions.`}</p>
            <button className="btn primary" disabled={locked} onClick={() => save(confirm.action, confirm.action === "merge" ? { source_id: row.id, target_id: confirm.target, expected_revision: row.revision } : { inspector_id: row.id, expected_revision: row.revision })}>Confirm {confirm.action}</button>
          </div>}
          <button className="btn sm" disabled={locked} onClick={() => { setSelected(null); setConfirm(null); }}>Cancel</button>
        </div>}
      </div>)}
    </>}
  </Card>;
}

export function SharedInspectorBook({ sale = "26R2R" }) {
  const book = useBook(`/collections/admin.sale_inspectors.list@1/?${new URLSearchParams({ sale })}`, value => value.sale === sale);
  const [text, setText] = React.useState("");
  const [review, setReview] = React.useState(null);
  React.useEffect(() => { if (book.data) { setText(book.data.names.join("\n")); setReview(null); } }, [book.data]);
  const locked = book.busy || Boolean(book.pending) || !book.data;
  const names = [...new Map(text.split("\n").map(value => value.trim()).filter(Boolean).map(value => [value.replace(/\s+/g, " ").toLowerCase(), value])).values()];
  return <Card title="Shared inspector suggestions" subtitle={`${sale} · offered to every vendor in this sale`}>
    <p>Paste one inspector per line. This list contains suggestions only; vendors keep their own names and inspection history private.</p>
    <BookStatus book={book}/>
    {book.data && <>
      <label className="field">Inspector names<textarea rows={14} value={text} disabled={locked || !book.data.canReplace} onChange={event => { setText(event.target.value); setReview(null); }}/></label>
      <p>{names.length} names · maximum 2,000</p>
      <button className="btn" disabled={locked || !book.data.canReplace || names.length > 2000 || names.some(name => name.length > 255)} onClick={() => setReview({ names, digest: book.data.digest })}>Review changes</button>
      {review && <div role="region" aria-label="Review shared inspector list">
        <p>{review.names.filter(name => !book.data.names.includes(name)).length} additions or spelling changes; {book.data.names.filter(name => !review.names.includes(name)).length} old suggestions retired. Private vendor books and past inspections are unchanged.</p>
        <button className="btn primary" disabled={locked} onClick={() => book.save({ path: "/actions/admin.sale_inspectors.replace@2/", input: { sale, names: review.names, expected_digest: review.digest, provenance: "inglis-admin" }, key: window.crypto.randomUUID() })}>Save shared list</button>
        <button className="btn" disabled={locked} onClick={() => setReview(null)}>Cancel</button>
      </div>}
    </>}
  </Card>;
}
